import { Injectable, OnDestroy } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import { AddressSetToCartService, CheckoutDataService, UserService } from '@app/services';
import { BaseSiteToBrandService } from 'haba-base-components';
import {
  ActiveCartService,
  AuthService,
  RoutingConfigService,
  StatePersistenceService,
  UserAddressService,
} from 'haba-core';
import { CartAddressUpdate } from 'haba-emporix';
import { CheckoutConfigService, CheckoutStepService } from 'haba-featurelibs/checkout/components';
import { CheckoutStepType } from 'haba-featurelibs/checkout/root';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class HabaExpressCheckoutGuard implements OnDestroy {
  private onDestroy$ = new Subject();

  constructor(
    protected router: Router,
    protected routingConfigService: RoutingConfigService,
    protected checkoutStepService: CheckoutStepService,
    protected checkoutConfigService: CheckoutConfigService,
    protected activeCartService: ActiveCartService,
    protected statePersistenceService: StatePersistenceService,
    protected authService: AuthService,
    private checkoutDataService: CheckoutDataService,
    private addressSetToCartService: AddressSetToCartService,
    private userAddressService: UserAddressService,
    private userService: UserService,
    private baseSiteToBrandService: BaseSiteToBrandService
  ) {}

  canActivate(): Observable<boolean | UrlTree> {
    if (this.activeCartService.isGuestCart()) {
      return this.checkCartData();
    }

    if (this.checkoutConfigService.isExpressCheckout() && !this.activeCartService.isGuestCart()) {
      return this.setAddresses().pipe(switchMap(() => this.checkCartData()));
    } else {
      return of(
        this.router.parseUrl(
          this.routingConfigService.getRouteConfig('checkoutBillingAddress').paths?.[0]
        )
      );
    }
  }

  checkCartData(): Observable<boolean | UrlTree> {
    return this.checkoutDataService.getCheckoutData().pipe(
      takeUntil(this.onDestroy$),
      switchMap((checkoutData) => {
        if (
          !checkoutData?.paymentAddress ||
          Object.keys(checkoutData.paymentAddress).length === 0 ||
          !checkoutData?.deliveryAddress ||
          Object.keys(checkoutData.deliveryAddress).length === 0
        ) {
          return of(
            this.router.parseUrl(
              this.routingConfigService.getRouteConfig('checkoutBillingAddress').paths?.[0]
            )
          );
        }

        if (!checkoutData?.paymentDetails?.isValid || !checkoutData?.paymentMode?.code) {
          const paymentTypeRoute = this.checkoutStepService.getCheckoutStepRoute(
            CheckoutStepType.PAYMENT_DETAILS
          );
          return of(
            this.router.parseUrl(
              this.routingConfigService.getRouteConfig(paymentTypeRoute).paths?.[0]
            )
          );
        }
        // If payment mode is 'invoice', only then check allowInvoicePayment()
        if (checkoutData.paymentMode.code === 'invoice') {
          return this.userService.allowInvoicePayment().pipe(
            map((response) => {
              if (!response.allow) {
                const paymentTypeRoute = this.checkoutStepService.getCheckoutStepRoute(
                  CheckoutStepType.PAYMENT_DETAILS
                );
                return this.router.parseUrl(
                  this.routingConfigService.getRouteConfig(paymentTypeRoute).paths?.[0]
                );
              }
              return true;
            })
          );
        }
        return of(true);
      })
    );
  }

  setAddresses(): Observable<CartAddressUpdate> {
    this.userAddressService.loadAddresses();
    return combineLatest([
      this.userAddressService.getAddressesLoading().pipe(
        filter((loading) => !loading),
        switchMap(() => this.userAddressService.getAddresses())
      ),
      this.checkoutDataService.getCheckoutData(),
      this.baseSiteToBrandService.currentCountryCode(),
      this.userAddressService.getInvoiceRecipients(),
    ]).pipe(
      take(1),
      switchMap(([addresses, checkoutData, currentCountryCode, invoiceRecipients]) => {
        let shippingAddress =
          addresses?.find(
            (address) =>
              address.defaultDeliveryAddress && address.country?.isocode === currentCountryCode
          ) ||
          addresses?.find(
            (address) => address.shippingAddress && address.country?.isocode === currentCountryCode
          );

        let billingAddress = addresses?.find(
          (address) => address.billingAddress && !address.habaPayerAddress
        );
        let sponsorAddress = addresses?.find((address) => address.habaPayerAddress);

        if (
          (checkoutData?.paymentAddress?.id?.length > 0 &&
            this.checkoutDataService.isBillingAddressValid(checkoutData?.paymentAddress)) ||
          !this.checkoutDataService.isBillingAddressValid(billingAddress)
        ) {
          billingAddress = undefined;
        }

        // the carrier and the company billing address are one pair: the carrier becomes
        // billingAddress and the company one moves to companyBillingAddress. Sending the
        // carrier alone makes the middleware reset companyBillingAddress to an empty
        // address, so when the billing is kept, the carrier is kept with it
        const invoiceRecipientSponsor = sponsorAddress;
        if (!billingAddress) {
          sponsorAddress = undefined;
        }

        if (
          (checkoutData?.deliveryAddress?.id?.length > 0 &&
            this.checkoutDataService.isShippingAddressValid(checkoutData?.deliveryAddress)) ||
          !this.checkoutDataService.isShippingAddressValid(shippingAddress)
        ) {
          shippingAddress = undefined;
        }

        // A carrier already becomes the cart's billing address, with the ordinary one
        // moved to companyBillingAddress. The deviating invoice recipient is chosen
        // the same way: prefer the carrier when it holds the email, so the summary
        // shows what the customer's record already says without them opening a form.
        const invoiceRecipient =
          (invoiceRecipientSponsor?.invoiceEmailAddress ? invoiceRecipientSponsor : undefined) ??
          invoiceRecipients?.find((recipient) => !!recipient.invoiceEmailAddress);

        return shippingAddress || billingAddress || invoiceRecipient
          ? this.addressSetToCartService.setShippingAndBillingAddress(
              shippingAddress,
              billingAddress,
              sponsorAddress,
              checkoutData?.invoiceAddress?.email ? undefined : invoiceRecipient
            )
          : of(null);
      })
    );
  }

  ngOnDestroy() {
    this.onDestroy$.next(undefined);
    this.onDestroy$.complete();
  }
}
